/**
 * T8002 · Hysteresis filter — 3 s settled window.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1442 — "Hysteresis collapses connectivity flapping shorter than 3_000 ms".
 *
 * WHY THIS EXISTS
 * ---------------
 * Sub-3-second WS hiccups (carrier handoff, brief packet loss) are flap noise.
 * Without hysteresis, the connectivity FSM would oscillate CONNECTED →
 * DEGRADED_NO_NATS → CONNECTED inside a second; the UI pill would strobe; the
 * reconnect ladder would fire on an already-healthy socket; downstream
 * dashboards would log thousands of meaningless transitions per hour.
 *
 * The filter collapses every raw observation that does NOT hold for at least
 * `windowMs` (default 3 000 ms). `connectivity:change` fires once — at
 * `settledAt` — carrying both the FIRST raw observation time (`observedAt`)
 * and the window-close time (`settledAt`). Telemetry uses `observedAt` for
 * duration math; UI uses `settledAt` for pill latency.
 *
 * EMPIRICAL ANCHOR
 * ----------------
 * Validated at `/tmp/morfi-sdk-connectivity-proto/claim4.ts`: emit fires 3 s
 * AFTER the final raw observation, NOT at it.
 *
 * NOT THE RECONNECT LADDER
 * ------------------------
 * Reconnect logic does NOT respect hysteresis — the wire layer reconnects
 * the moment it sees a disconnect. Only the user-visible state machine +
 * pill respect the settled window.
 */

import type { ConnectivityState, ConnTransition } from './state-machine.js';

/** Default settled-window length per spec L1442. */
export const DEFAULT_HYSTERESIS_WINDOW_MS = 3_000;

export interface HysteresisFilter {
  /**
   * Observe a raw signal. If `state` differs from the currently settled
   * state, the filter arms a `windowMs` timer; emission only fires if the
   * candidate state survives the entire window.
   */
  observe(state: ConnectivityState, cause: string): void;
  /** The most recently SETTLED state — never returns a pending candidate. */
  current(): ConnectivityState;
  /**
   * Drop any pending (un-settled) transition + its timer. Call on teardown so
   * an orchestrator replaced mid-settle (e.g. a reconnect that swaps the NATS
   * client) cannot fire a stale transition into the store after a fresh
   * orchestrator has already taken over.
   */
  cancel(): void;
}

export interface CreateHysteresisFilterOptions {
  readonly windowMs?: number;
  readonly now?: () => number;
  readonly emit: (t: ConnTransition) => void;
  readonly initial?: ConnectivityState;
}

/**
 * Factory. Each instance owns one pending state + one timer handle. No
 * module-level state — multiple filters may coexist (tests rely on this).
 */
export function createHysteresisFilter(opts: CreateHysteresisFilterOptions): HysteresisFilter {
  const windowMs = opts.windowMs ?? DEFAULT_HYSTERESIS_WINDOW_MS;
  const now = opts.now ?? ((): number => performance.now());

  let settled: ConnectivityState = opts.initial ?? 'CONNECTED';
  let pending: ConnectivityState | null = null;
  let pendingObservedAt = 0;
  let pendingCause = '';
  let timer: ReturnType<typeof setTimeout> | null = null;

  function fire(): void {
    timer = null;
    if (pending === null) return;
    if (pending === settled) {
      pending = null;
      return;
    }
    const transition: ConnTransition = {
      from: settled,
      to: pending,
      observedAt: pendingObservedAt,
      settledAt: now(),
      cause: pendingCause,
    };
    settled = pending;
    pending = null;
    opts.emit(transition);
  }

  return {
    observe(state: ConnectivityState, cause: string): void {
      // Re-observing the settled state cancels any pending transition.
      if (state === settled) {
        pending = null;
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
        return;
      }
      // Re-observing the same pending state does NOT reset the timer —
      // the window measures continuous presence, not re-arrival.
      if (state === pending) return;

      pending = state;
      pendingObservedAt = now();
      pendingCause = cause;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(fire, windowMs);
    },
    current(): ConnectivityState {
      return settled;
    },
    cancel(): void {
      pending = null;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}
